import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import translations from "./translations";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "sq");

  const changeLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations["en"]?.[key] || key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang: changeLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be inside LanguageProvider");
  return ctx;
}

// Flag SVG components
export function AlbanianFlag({ className = "w-5 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="20" rx="2" fill="#E41E20"/>
      <path d="M14 4L16.5 7H18L17 9L18.5 11H16.5L14 14L11.5 11H9.5L11 9L10 7H11.5L14 4Z" fill="black"/>
      <path d="M12 8.5C12 8.5 13 7.5 14 7.5C15 7.5 16 8.5 16 8.5" stroke="black" strokeWidth="0.5"/>
    </svg>
  );
}

export function EnglishFlag({ className = "w-5 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="20" rx="2" fill="#012169"/>
      <path d="M0 0L28 20M28 0L0 20" stroke="white" strokeWidth="3"/>
      <path d="M0 0L28 20M28 0L0 20" stroke="#C8102E" strokeWidth="1.5"/>
      <path d="M14 0V20M0 10H28" stroke="white" strokeWidth="5"/>
      <path d="M14 0V20M0 10H28" stroke="#C8102E" strokeWidth="3"/>
    </svg>
  );
}

export function ItalianFlag({ className = "w-5 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="9.33" height="20" fill="#009246"/>
      <rect x="9.33" width="9.34" height="20" fill="#FFFFFF"/>
      <rect x="18.67" width="9.33" height="20" fill="#CE2B37"/>
      <rect width="28" height="20" rx="2" fill="none" stroke="#00000010"/>
    </svg>
  );
}

// Language Switcher Component
export function LanguageSwitcher({ variant = "default" }) {
  const { lang, setLang } = useLanguage();

  if (variant === "dark") {
    return (
      <div className="flex items-center gap-1 p-1 rounded-lg bg-white/10 border border-white/10" data-testid="language-switcher-dark">
        <button
          onClick={() => setLang("sq")}
          data-testid="lang-btn-sq"
          aria-label="Shqip"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
            lang === "sq" ? "bg-white/20 text-white" : "text-gray-400 hover:text-white"
          }`}
        >
          <AlbanianFlag className="w-4 h-3" />
          <span className="hidden sm:inline">Shqip</span>
        </button>
        <button
          onClick={() => setLang("en")}
          data-testid="lang-btn-en"
          aria-label="English"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
            lang === "en" ? "bg-white/20 text-white" : "text-gray-400 hover:text-white"
          }`}
        >
          <EnglishFlag className="w-4 h-3" />
          <span className="hidden sm:inline">EN</span>
        </button>
        <button
          onClick={() => setLang("it")}
          data-testid="lang-btn-it"
          aria-label="Italiano"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
            lang === "it" ? "bg-white/20 text-white" : "text-gray-400 hover:text-white"
          }`}
        >
          <ItalianFlag className="w-4 h-3" />
          <span className="hidden sm:inline">IT</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5 p-0.5 sm:p-1 rounded-lg bg-gray-100 border border-gray-200" data-testid="language-switcher">
      <button
        onClick={() => setLang("sq")}
        data-testid="lang-btn-sq"
        aria-label="Shqip"
        className={`flex items-center gap-1 px-1.5 sm:px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
          lang === "sq" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <AlbanianFlag className="w-4 h-3" />
        <span className="hidden sm:inline">SQ</span>
      </button>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-btn-en"
        aria-label="English"
        className={`flex items-center gap-1 px-1.5 sm:px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
          lang === "en" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <EnglishFlag className="w-4 h-3" />
        <span className="hidden sm:inline">EN</span>
      </button>
      <button
        onClick={() => setLang("it")}
        data-testid="lang-btn-it"
        aria-label="Italiano"
        className={`flex items-center gap-1 px-1.5 sm:px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
          lang === "it" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <ItalianFlag className="w-4 h-3" />
        <span className="hidden sm:inline">IT</span>
      </button>
    </div>
  );
}
